import { useI18n } from "@/context/I18nContext";
import { Truck, ShoppingBag, CreditCard, BadgeCheck } from "lucide-react";
import { PosCard, RankTable } from "./PosTable";
import { OPS } from "@/constants/testIds";

const PI = OPS.posInsights;

const PAYMENT_KEYS = ["cash", "mada", "visa", "mcard", "network", "bank_transfer", "tips", "service"];
const PAYMENT_LABEL = {
    cash: { ar: "نقدي", en: "Cash" },
    mada: { ar: "مدى", en: "Mada" },
    visa: { ar: "فيزا", en: "Visa" },
    mcard: { ar: "ماستركارد", en: "MasterCard" },
    network: { ar: "شبكة", en: "Network" },
    bank_transfer: { ar: "تحويل بنكي", en: "Bank transfer" },
    tips: { ar: "إكراميات", en: "Tips" },
    service: { ar: "خدمة", en: "Service" },
};

export default function PosChannelsPanel({ data }) {
    const { t, lang } = useI18n();
    const nameOf = (r) => (lang === "ar" ? r.name_ar || r.name_en : r.name_en || r.name_ar);

    const payments = PAYMENT_KEYS
        .map((k) => ({ key: k, name: PAYMENT_LABEL[k][lang === "ar" ? "ar" : "en"], total: data?.totals?.payments?.[k] ?? 0 }))
        .filter((r) => r.total > 0);

    return (
        <div className="grid grid-cols-1 gap-4">
            <PosCard icon={Truck} title={t("pi_apps_title")} desc={t("pi_apps_desc")}>
                <RankTable
                    testId={PI.appsTable}
                    rows={data?.apps || []}
                    columns={[
                        { key: "name", label: t("pi_apps_title"), strong: true },
                        { key: "orders", label: t("pi_col_orders"), type: "int" },
                        { key: "total", label: t("pi_col_total"), type: "num" },
                        { key: "delivery_price", label: t("pi_col_delivery_fee"), type: "num" },
                        { key: "paid_total", label: t("pi_col_paid"), type: "num" },
                        { key: "unpaid_total", label: t("pi_col_unpaid"), type: "num" },
                    ]}
                    shareKey="total"
                    rowTestId={(r, i) => `pos-insights-app-row-${r.id ?? i}`}
                />
            </PosCard>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <PosCard icon={ShoppingBag} title={t("pi_order_types")}>
                    <RankTable
                        testId={PI.orderTypes}
                        rows={(data?.order_types || []).map((r) => ({ ...r, name: nameOf(r) }))}
                        columns={[
                            { key: "name", label: t("pi_order_types"), strong: true },
                            { key: "orders", label: t("pi_col_orders"), type: "int" },
                            { key: "total", label: t("pi_col_total"), type: "num" },
                        ]}
                        shareKey="total"
                        rowTestId={(r, i) => `pos-insights-order-type-${r.id ?? i}`}
                    />
                </PosCard>
                <PosCard icon={CreditCard} title={t("pi_payments")}>
                    <RankTable
                        testId={PI.payments}
                        rows={payments}
                        columns={[
                            { key: "name", label: t("pi_payments"), strong: true },
                            { key: "total", label: t("pi_col_total"), type: "num" },
                        ]}
                        shareKey="total"
                        rowTestId={(r) => `pos-insights-payment-${r.key}`}
                    />
                </PosCard>
                <PosCard icon={BadgeCheck} title={t("pi_pay_states")}>
                    <RankTable
                        testId={PI.payStates}
                        rows={(data?.pay_states || []).map((r) => ({ ...r, name: nameOf(r) }))}
                        columns={[
                            { key: "name", label: t("pi_pay_states"), strong: true },
                            { key: "orders", label: t("pi_col_orders"), type: "int" },
                            { key: "total", label: t("pi_col_total"), type: "num" },
                        ]}
                        shareKey="total"
                        rowTestId={(r, i) => `pos-insights-pay-state-${r.id ?? i}`}
                    />
                </PosCard>
            </div>
        </div>
    );
}
